import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@design-system/shadcn";
import { cn } from "@design-system/utils";
import { flexRender } from "@tanstack/react-table";
import React from "react";

import { useDataTableContext } from "../hooks";

const DEFAULT_COLUMN_SIZE = 150;

function getCellProps(align?: "left" | "center" | "right", size?: number) {
  return {
    className: cn(
      align === "right" && "text-right",
      align === "center" && "text-center"
    ),
    style: size !== DEFAULT_COLUMN_SIZE ? { width: size } : undefined,
  };
}

function TableView() {
  const { table, onRowClick } = useDataTableContext<unknown, unknown>();

  return (
    <Table className="my-4">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const cellProps = getCellProps(
                header.column.columnDef.meta?.align,
                header.getSize()
              );

              return (
                <TableHead key={header.id} {...cellProps}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              onClick={() => onRowClick?.(row.original)}
              className={
                onRowClick
                  ? "hover:bg-shape-neutral-outlined-background-hover cursor-pointer"
                  : undefined
              }
            >
              {row.getVisibleCells().map((cell) => {
                const cellProps = getCellProps(
                  cell.column.columnDef.meta?.align,
                  cell.column.getSize()
                );

                return (
                  <TableCell key={cell.id} {...cellProps}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={table.getAllColumns().length}
              className="h-24 text-center"
            >
              {/* empty by design; host controls empty state text */}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export { TableView as Table };
